import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Placement = 'top' | 'bottom' | 'left' | 'right';

/**
 * Tooltip léger — remplace antd Tooltip / MUI Tooltip.
 * Apparaît au survol avec un délai court.
 */
const Tooltip = (props: { title: ReactNode; children: ReactNode; placement?: Placement }) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const ref = useRef<HTMLDivElement>(null);
    const timer = useRef<ReturnType<typeof setTimeout>>();

    const show = () => {
        timer.current = setTimeout(() => {
            if (!ref.current) return;
            const r = ref.current.getBoundingClientRect();
            const p = props.placement || 'top';
            setCoords({
                top: p === 'top' ? r.top : p === 'bottom' ? r.bottom : r.top + r.height / 2,
                left: p === 'left' ? r.left : p === 'right' ? r.right : r.left + r.width / 2,
            });
            setVisible(true);
        }, 250);
    };

    const hide = () => {
        clearTimeout(timer.current);
        setVisible(false);
    };

    useEffect(() => () => clearTimeout(timer.current), []);

    const p = props.placement || 'top';
    const posClass = {
        top: '-translate-x-1/2 -translate-y-full -mt-1.5',
        bottom: '-translate-x-1/2 mt-1.5',
        left: '-translate-x-full -translate-y-1/2 -ml-1.5',
        right: '-translate-y-1/2 ml-1.5',
    }[p];

    return (
        <>
            <div ref={ref} className="inline-flex" onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
                {props.children}
            </div>
            {visible && props.title && createPortal(
                <div
                    className={`fixed z-[9999] px-2.5 py-1.5 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-nowrap pointer-events-none ${posClass}`}
                    style={{ top: coords.top, left: coords.left }}
                    role="tooltip"
                >
                    {props.title}
                </div>,
                document.body
            )}
        </>
    );
};

export default Tooltip;
